import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, Clock, PenLine } from "lucide-react";

const ICON = {
  pending: <Clock className="h-4 w-4 text-slate-400" />,
  reading: <Loader2 className="h-4 w-4 animate-spin text-sky-600" />,
  read: <CheckCircle2 className="h-4 w-4 text-emerald-600" />,
  failed: <XCircle className="h-4 w-4 text-red-600" />,
  manual: <PenLine className="h-4 w-4 text-slate-600" />,
};

const PageRow = ({ p, hasFile, onRetry, onManual }) => {
  const ref = useRef(null);
  return (
    <li data-testid={`page-status-${p.key}`} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
      {ICON[p.status]}
      <span className="font-medium">{p.plant} · Page {p.page}</span>
      <span className="text-slate-500 truncate max-w-48">{p.name}</span>
      <span className="text-slate-600">
        {p.status === "read" && `${p.count} line(s) read`}
        {p.status === "reading" && "Reading + double-checking (about 1–2 min)…"}
        {p.status === "pending" && "Waiting"}
        {p.status === "manual" && "Marked as entered manually"}
        {p.status === "failed" && <span className="text-red-700">{p.error}</span>}
      </span>
      {p.issues?.length > 0 && <span className="text-xs text-amber-700">Unreadable: {p.issues.join("; ")}</span>}
      {p.status === "failed" && (
        <span className="flex gap-1 ml-auto">
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && onRetry(p, e.target.files[0])} />
          <Button size="sm" variant="outline" className="h-7" data-testid={`page-retry-${p.key}`} onClick={() => (hasFile ? onRetry(p) : ref.current?.click())}>{hasFile ? "Retry" : "Upload again"}</Button>
          <Button size="sm" variant="ghost" className="h-7" data-testid={`page-manual-${p.key}`} onClick={() => onManual(p)}>I'll type its lines myself</Button>
        </span>
      )}
    </li>
  );
};

export const PagesPanel = ({ pages, files, onRetry, onManual }) => {
  const done = pages.filter((p) => ["read", "manual"].includes(p.status)).length;
  return (
    <section data-testid="pages-panel" className="bg-white border rounded-xl p-4">
      <div className="font-semibold text-sm mb-1">Pages processed: <span data-testid="pages-done-count">{done}</span> / {pages.length}</div>
      <ul className="divide-y">{pages.map((p) => <PageRow key={p.key} p={p} hasFile={!!files[p.key]} onRetry={onRetry} onManual={onManual} />)}</ul>
    </section>
  );
};
